import {
  Button,
  Container,
  FormControl,
  FormGroup,
  FormHelperText,
  FormLabel,
  Grid,
  List,
  ListItem,
  makeStyles,
  Typography,
} from "@material-ui/core";
import { NavigateNext } from "@material-ui/icons";
import { Field, Form, Formik } from "formik";
import { CheckboxWithLabel } from "formik-material-ui";
import React from "react";
import { Redirect } from "react-router-dom";
import * as Yup from "yup";
import { AuthContext } from "../Auth/AuthContextProvider";

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: "2vh",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  formRoot: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  errorText: {
    color: theme.palette.error.main,
  },
  nested: {
    marginLeft: theme.spacing(3),
  },
  actionButton: {
    marginTop: theme.spacing(1),
  },
  registerButton: {
    color: theme.palette.primary.contrastText,
  },
  houseBillHeaderText: {
    textAlign: "center",
    margin: "1rem 0 0.5rem 0",
    fontSize: "1.3rem",
    fontWeight: 500,
  },
  houseBillText: {
    border: "2px solid #767676",
    padding: "0 1rem 1rem 1rem",
    borderRadius: "0.3rem",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
  },
}));

const registerRequirements = [
  {
    name: "adopteeBornInUtah",
    text: "The adoptee was born in Utah. (For adoptees born in other states, please contact that birth state.)",
  },
  {
    name: "understandFees",
    text: "I understand there is a $25 fee per registration to join the Utah Adoption Registry, unless I have previously joined. If there is a change in information used for matching which requires a new search, an update fee of $10 may be required.",
  },
  {
    name: "matchNotGuaranteed",
    text: "I understand that joining the voluntary registry does not guarantee that a match will be found.",
  },
];

export default function Home({ location }) {
  const classes = useStyles();
  const authContext = React.useContext(AuthContext);

  return (
    <Container role="main" className={classes.root}>
      <Formik
        initialValues={{
          userIdentity1: false,
          userIdentity2: false,
          userIdentity3: false,
          adopteeBornInUtah: false,
          understandFees: false,
          matchNotGuaranteed: false,
        }}
        validationSchema={Yup.object().shape({
          userIdentity: Yup.mixed().test(
            "one-of-above-required",
            "Please check any option that describes you",
            function () {
              return this.parent.userIdentity1 || this.parent.userIdentity2 || this.parent.userIdentity3;
            }
          ),
          adopteeBornInUtah: Yup.boolean().isTrue("This must be checked to proceed"),
          understandFees: Yup.boolean().isTrue("This must be checked to proceed"),
          matchNotGuaranteed: Yup.boolean().isTrue("This must be checked to proceed"),
        })}
        onSubmit={(values) => {
          return authContext.navigateToUtahIDLogin();
        }}
      >
        {(formik) => (
          <Form className={classes.formRoot}>
            <Grid className={classes.houseBillText}>
              <Typography variant="h2" className={classes.houseBillHeaderText}>
                Latest News
              </Typography>
              <Typography>
                <a target="_blank" href="https://le.utah.gov/~2025/bills/static/HB0129.html">
                  Click here to read the house bill—H.B. 129
                </a>
                {" - "}
                Requests for non-certified birth records or mutual consent contact information will take up to 8 weeks.
              </Typography>
              <Typography style={{ marginTop: "1rem" }}>
                Provide the requested information in the form below so the Department of Health and Human Services
                (DHHS) Office of Vital Records and Statistics can process your request. The information you give us will
                only be used by DHHS to process your request, and if necessary, may also be accessed by contractors who
                are a part of the technical team. We will not be able to process your request without the necessary
                information. This data is part of the record series 28618.
              </Typography>
            </Grid>
            <List>
              {registerRequirements.map((req) => (
                <ListItem key={req.name}>
                  <FormControl>
                    <Field
                      component={CheckboxWithLabel}
                      type="checkbox"
                      name={req.name}
                      Label={{
                        label: req.text,
                      }}
                    />
                    {formik.touched[req.name] && formik.errors[req.name] && (
                      <FormHelperText className={classes.errorText}>{formik.errors[req.name]}</FormHelperText>
                    )}
                  </FormControl>
                </ListItem>
              ))}
              <ListItem>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Which of the following describes you?</FormLabel>
                  <FormGroup className={classes.nested}>
                    <Field
                      component={CheckboxWithLabel}
                      type="checkbox"
                      name="userIdentity1"
                      Label={{
                        label: "I am an adoptee over 18",
                      }}
                    />
                    <Field
                      component={CheckboxWithLabel}
                      type="checkbox"
                      name="userIdentity2"
                      Label={{
                        label: "I am over 18 and the sibling with a common birth parent of an adoptee",
                      }}
                    />
                    <Field
                      component={CheckboxWithLabel}
                      type="checkbox"
                      name="userIdentity3"
                      Label={{
                        label: "I am a birth parent of an adoptee",
                      }}
                    />
                    <FormHelperText className={classes.errorText}>{formik.errors.userIdentity}</FormHelperText>
                  </FormGroup>
                </FormControl>
              </ListItem>
            </List>
            <Grid className={classes.buttonContainer}>
              <Button
                color="default"
                variant="outlined"
                className={classes.actionButton}
                onClick={authContext.navigateToUtahIDLogin}
                type="button"
                style={{ marginRight: "1rem" }}
              >
                Already registered? Log In Here <NavigateNext />
              </Button>
              <Button
                color="primary"
                variant="contained"
                className={`${classes.actionButton} ${classes.registerButton}`}
                type="submit"
              >
                Register Now
              </Button>
            </Grid>
          </Form>
        )}
      </Formik>

      {authContext.isLoggedIn &&
        // Navigate to the route the user came from before log in, or default to their dashboard
        (authContext.isAdministrator ? (
          <Redirect to={location?.state?.from || "/admin"} />
        ) : (
          <Redirect to={location?.state?.from || "/my-registrations"} />
        ))}
    </Container>
  );
}
